/**
 * The free period's ceiling, as the screens read it.
 *
 * While a subscription trials, the backend sizes `interaction` and
 * `partnership_search` by the free-period ceiling (200 and 3 per seat) and
 * says so with `trial_cap`. The notices below are driven by those figures
 * alone (RN-06, RN-07): the warning at 80%, the block at 100%.
 */

import type { MeterSource } from './plan-tab-model';

/** The two metered quotas a trial caps. */
export type TrialKind = 'interaction' | 'partnershipSearch';

/** What, if anything, the customer should be told. */
export type NoticeLevel = 'none' | 'warn' | 'block';

/** Share of the ceiling at which the warning appears (RN-07). */
export const TRIAL_WARN_RATIO = 0.8;

/** The `type` / `error` code the backend answers with past the ceiling. */
export const TRIAL_QUOTA_EXHAUSTED = 'trial_quota_exhausted';

/** The slice of the usage payload the trial notices read. */
export interface TrialUsage {
  trial_cap?: boolean;
  trial_ends_at?: string | null;
  interaction: MeterSource;
  partnership_search: MeterSource;
}

const USAGE_KEY = {
  interaction: 'interaction',
  partnershipSearch: 'partnership_search',
} as const;

/**
 * Decides the notice for one quota.
 *
 * Partnership searches skip the warning: 3 is too few to pre-announce (RN-07).
 *
 * @param kind - Which quota.
 * @param used - Units consumed.
 * @param total - The ceiling.
 * @returns none, warn (80% up to the last unit) or block (100%).
 */
export function noticeLevel(kind: TrialKind, used: number, total: number): NoticeLevel {
  if (!(total > 0)) return 'none';
  if (used >= total) return 'block';
  const warns = kind === 'interaction' && used / total >= TRIAL_WARN_RATIO;
  return warns ? 'warn' : 'none';
}

/**
 * The notice a usage payload earns for one quota, only while trialing.
 *
 * @param usage - The usage summary, or null when unreadable.
 * @param kind - Which quota.
 * @returns The notice level.
 */
export function trialNoticeLevel(usage: TrialUsage | null | undefined, kind: TrialKind): NoticeLevel {
  if (!usage?.trial_cap) return 'none';
  const source = usage[USAGE_KEY[kind]];
  return noticeLevel(kind, source.used, source.total);
}

/**
 * Whether an error is the backend refusing a trial-capped consumption.
 *
 * The agent answers `{"error": "trial_quota_exhausted"}`; the partnership
 * search answers problem+json with `type: "trial_quota_exhausted"`.
 *
 * @param err - Anything thrown by a request.
 * @returns True for a 402 trial_quota_exhausted.
 */
export function isTrialQuotaExhausted(err: unknown): boolean {
  const { status, body } = (err ?? {}) as { status?: unknown; body?: Record<string, unknown> | null };
  if (status !== 402) return false;
  return body?.error === TRIAL_QUOTA_EXHAUSTED || body?.type === TRIAL_QUOTA_EXHAUSTED;
}

/**
 * Writes a `YYYY-MM-DD` date for the reader's locale (RN-17: a date, never
 * a countdown).
 *
 * Parsed by parts so the day cannot slide across a timezone boundary.
 *
 * @param iso - The date, or null.
 * @param locale - BCP-47 tag.
 * @returns The formatted date, or null when absent or malformed.
 */
export function formatTrialDate(iso: string | null | undefined, locale: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(date);
}
